/** Links are navigation requests, never proof of thread ownership. */
export function isChatHash(hash: string): boolean {
  return ['', '#', '#/', '#/workspace/chat'].includes(hash.split('?')[0]);
}

/**
 * A session change normally starts a fresh chat (another account must never inherit the open one). Signing in from
 * signed-out while the address names a chat is not a change of account: the link the person opened stays, and
 * navigation opens that chat once the session is known.
 */
export function keepsLinkedChatOnSignIn(previousUserId: string | null | undefined, hash: string) {
  return !previousUserId && chatIdFromHash(hash) !== null;
}

export function chatIdFromHash(hash: string): string | null {
  const [path, query] = hash.split('?');
  if (path !== '#/workspace/chat') return null;
  const id = new URLSearchParams(query ?? '').get('thread');
  return id && /^[A-Za-z0-9_-]{1,128}$/.test(id) ? id : null;
}

/** Keep canvas context when a new conversation becomes durable. A project's home becomes
 * the chat started there: the chat names its project from then on, not the link. */
export function chatHash(id?: string | null, hash = '#/workspace/chat'): string {
  const params = new URLSearchParams(hash.split('?')[1] ?? '');
  if (id) {
    params.set('thread', id);
    params.delete('project');
  } else params.delete('thread');
  const query = params.toString();
  return `#/workspace/chat${query ? `?${query}` : ''}`;
}

/** All workspace navigation participates in the browser's own history. The
 * dispatched hashchange carries `inApp` so a listener can tell the app's own
 * write from a browser traversal (Back/Forward, a hash edit). */
export function writeWorkspaceHash(
  hash: string,
  mode: 'push' | 'replace' = 'push',
  state: Record<string, unknown> | null = null,
) {
  if (location.hash === hash) return;
  history[mode === 'push' ? 'pushState' : 'replaceState'](
    state,
    '',
    location.pathname + location.search + hash,
  );
  window.dispatchEvent(new CustomEvent('hashchange', { detail: { inApp: true } }));
}

export function isAppHashChange(event: Event) {
  return (event as CustomEvent<{ inApp?: boolean } | null>).detail?.inApp === true;
}

/** Prevent a slow previous fetch from replacing a newer chat/new-chat choice. */
export function createThreadNavigation() {
  let generation = 0;
  return {
    begin() {
      const current = ++generation;
      return () => current === generation;
    },
    cancel() {
      generation += 1;
    },
  };
}
